import type { AxiosResponse } from 'axios'

const TOKEN_KEY = 'jwt_token'
const TOKEN_EXPIRY_KEY = 'jwt_expiry'

export interface LoginCredentials {
  email: string
  password: string
}

export interface LoginResponse {
  token: string
  user: {
    id: string
    email: string
    name: string
    role: 'admin' | 'accountant' | 'user'
    firm_id?: string
    locale: string
  }
}

export interface DecodedToken {
  user_id: string
  email: string
  role: 'admin' | 'accountant' | 'user'
  firm_id?: string
  exp: number
  iat: number
}

class AuthService {
  private refreshTimer?: number

  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY)
  }

  setToken(token: string): void {
    localStorage.setItem(TOKEN_KEY, token)
    const decoded = this.decodeToken(token)
    if (decoded) {
      const expiryDate = new Date(decoded.exp * 1000)
      localStorage.setItem(TOKEN_EXPIRY_KEY, expiryDate.toISOString())
      this.scheduleTokenRefresh(decoded.exp)
    }
  }

  removeToken(): void {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(TOKEN_EXPIRY_KEY)
    this.clearRefreshTimer()
  }

  isTokenExpired(): boolean {
    const token = this.getToken()
    if (!token) return true

    const decoded = this.decodeToken(token)
    if (!decoded) return true

    const now = Date.now() / 1000
    return decoded.exp < now
  }

  decodeToken(token: string): DecodedToken | null {
    try {
      const base64Url = token.split('.')[1]
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      )
      return JSON.parse(jsonPayload)
    } catch (error) {
      console.error('Failed to decode token:', error)
      return null
    }
  }

  scheduleTokenRefresh(expiry: number): void {
    this.clearRefreshTimer()

    const now = Date.now() / 1000
    const timeUntilExpiry = expiry - now
    const refreshTime = Math.max(0, (timeUntilExpiry - 300) * 1000)

    if (refreshTime > 0) {
      this.refreshTimer = window.setTimeout(() => {
        this.refreshToken()
      }, refreshTime)
    }
  }

  clearRefreshTimer(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer)
      this.refreshTimer = undefined
    }
  }

  async refreshToken(): Promise<void> {
    console.log('Token refresh would happen here - not implemented in MVP')
  }

  getAuthHeader(): { Authorization: string } | {} {
    const token = this.getToken()
    return token ? { Authorization: `Bearer ${token}` } : {}
  }

  getUserFromToken(): DecodedToken['user_id'] | null {
    const token = this.getToken()
    if (!token) return null

    const decoded = this.decodeToken(token)
    return decoded ? decoded.user_id : null
  }

  getRoleFromToken(): DecodedToken['role'] | null {
    const token = this.getToken()
    if (!token) return null

    const decoded = this.decodeToken(token)
    return decoded ? decoded.role : null
  }

  hasRole(requiredRole: 'admin' | 'accountant' | 'user'): boolean {
    const userRole = this.getRoleFromToken()
    if (!userRole) return false

    const roleHierarchy: Record<string, number> = {
      admin: 3,
      accountant: 2,
      user: 1
    }

    return roleHierarchy[userRole] >= roleHierarchy[requiredRole]
  }

  isAdmin(): boolean {
    return this.getRoleFromToken() === 'admin'
  }

  isAccountant(): boolean {
    const role = this.getRoleFromToken()
    return role === 'accountant' || role === 'admin'
  }

  isUser(): boolean {
    return this.getRoleFromToken() === 'user'
  }
}

export default new AuthService()
